import {
  GET_PROJECTS_ASSIGNED_TO_SERVICE,
  GET_MAPPING_OPTIONS_URL,
  SUBMIT_MAPPING_URL,
  GET_QUESTIONS_MAPPED_URL,
  get_headers
} from '@/utils/constants/constants.js';
import formatting_mixins from "@/utils/mixins/formatting_mixins";
import alert_mixin from '@/utils/mixins/alert_mixin';
import axios from 'axios';

export default {
  name: "ConfigureVerificationQuestions",
  mixins: [alert_mixin, formatting_mixins],
  data: () => ({
      verification_services: [],
      selected_services: [],
      mappings: {},
      questions: {},
      loading: false,
      error: null,
      loading_questions: {},
      form_id: null,
      saving_services: {},
      project_id: sessionStorage.getItem('project_id'),
  }),
  mounted(){
    this.get_form_id_from_session_storage();
    this.fetch_verification_services();
  },
  methods: {
    get_form_id_from_session_storage() {
      const form_id = sessionStorage.getItem('form_id');
      if (form_id) {
        this.form_id = form_id;
      } else {
        this.show_error('Form ID not found. Please ensure you are accessing this page from a valid form.');
      }
    },

    async fetch_verification_services() {
      this.loading = true;
      this.error = null;

      try {
        const response = await axios.get(GET_PROJECTS_ASSIGNED_TO_SERVICE, {
          headers: get_headers(),
          params: { project_id: this.project_id },
        });

        const parsed_response = typeof response.data === 'string'
          ? JSON.parse(response.data)
          : response.data;

        if (parsed_response.status === 'success' && parsed_response.data) {
          this.verification_services = parsed_response.data.map(service => ({
            id: service.id,
            name: service.name,
            description: service.description,
            price: service.price,
            required_fields: service.required_fields,
            system_verification_fields: service.system_verification_fields,
            date_created: service.date_created,
            is_active: service.is_active
          }));

          this.initialize_mappings();
          
          // Fetch all existing mappings in parallel
          const mapping_promises = this.verification_services.map(service => 
            this.fetch_existing_mappings(service.id)
          );
          await Promise.all(mapping_promises);

        } else {
          this.show_error(parsed_response.message || 'Failed to fetch services');
        }
      } catch (error) {
        this.error = 'Failed to load verification services. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    async fetch_questions_for_service(service_id) {
      if (Reflect.has(this.questions, service_id)) {
        return;
      }

      if (!this.form_id) {
        return;
      }

      Reflect.set(this.loading_questions, service_id, true);

      try {
        const params = {
          service_id: service_id,
          form_id: this.form_id
        };

        const response = await axios.get(GET_MAPPING_OPTIONS_URL, {
          headers: get_headers(),
          params: params
        });

        const parsed_response = typeof response.data === 'string'
          ? JSON.parse(response.data)
          : response.data;

        const questions = Array.isArray(parsed_response.questions) ? parsed_response.questions : [];
        Reflect.set(this.questions, service_id, questions);
      } catch (error) {
        Reflect.set(this.questions, service_id, []);
        this.show_error(`Failed to load questions for ${this.get_service_name(service_id)}. Please try again.`);
      } finally {
        Reflect.set(this.loading_questions, service_id, false);
      }
    },

    get_service_name(service_id) {
      const service = this.verification_services.find(s => s.id === service_id);
      return service ? service.name : 'this service';
    },

    async toggle_service(service_id) {
      const index = this.selected_services.indexOf(service_id);
      if (index > -1) {
        this.selected_services.splice(index, 1);
      } else {
        this.selected_services.push(service_id);
        await this.fetch_questions_for_service(service_id);
      }
    },

    initialize_mappings() {
      this.mappings = {};
      this.verification_services.forEach((service) => {
        Reflect.set(this.mappings, service.id, {});
        const combined_fields = [];
        service.required_fields.forEach(fieldName => combined_fields.push({ name: fieldName, type: "api" }));
        service.system_verification_fields.forEach(fieldName => combined_fields.push({ name: fieldName, type: "system" }));
        service.combined_fields = combined_fields;

        combined_fields.forEach(({ name }) => {
          if (!Reflect.has(this.mappings[service.id], name)) {
            Reflect.set(this.mappings[service.id], name, "");
          }
        });
      });
    },

    update_mapping() {
      // Placeholder for future logic
    },

    async save_single_service(service_id) {
      if (!this.form_id) {
        return;
      }

      const service = this.verification_services.find(s => s.id === service_id);
      if (!service) {
        this.show_error("Service not found.");
        return;
      }

      // Validate mappings for this service only
      const has_empty_mappings = service.combined_fields.some(({ name }) => {
        const mapping = Reflect.get(this.mappings, service_id) || {};
        const value = Object.prototype.hasOwnProperty.call(mapping, name)
          ? Reflect.get(mapping, name)
          : "";
        return !value;
      });

      if (has_empty_mappings) {
        this.show_error(`Please map all required fields for ${service.name}`);
        return;
      }

      Reflect.set(this.saving_services, service_id, true);

      try {
        const mappings = [];

            service.combined_fields.forEach(({ name, type }) => {
              const question_id = Reflect.get(Reflect.get(this.mappings, service_id), name);
              if (question_id) {
                mappings.push({
                  service_id,
                  form_id: parseInt(this.form_id, 10),
                  required_field: name,
                  question_id: parseInt(question_id, 10),
                  field_type: type
            });
          }
        });

        const payload = { mappings };

        const response = await axios.post(SUBMIT_MAPPING_URL, payload, {
          headers: get_headers()
        });

        const parsed_response = typeof response.data === 'string'
          ? JSON.parse(response.data)
          : response.data;

        if (parsed_response.status === 'success') {
          this.show_success(`${service.name} configuration saved successfully!`);
        } else {
          this.show_error(`Failed to save ${service.name}: ` + parsed_response.message);
        }
      } catch (error) {
        this.show_error(`Error saving ${service.name}. Please try again.`);
      } finally {
        Reflect.set(this.saving_services, service_id, false);
      }
    },

    cancel_service(service_id) {
      // Collapse the service card
      const index = this.selected_services.indexOf(service_id);
      if (index > -1) {
        this.selected_services.splice(index, 1);
      }
    },

    validate_mappings() {
      const services_with_missing_mappings = [];

      this.selected_services.forEach(service_id => {
        const service = this.verification_services.find(s => s.id === service_id);
        if (!service) return;

        const has_empty_mappings = service.combined_fields.some(({ name }) => {
          const mapping = Reflect.get(this.mappings, service_id) || {};
          const value = Object.prototype.hasOwnProperty.call(mapping, name)
            ? Reflect.get(mapping, name)
            : "";
          return !value;
        });

        if (has_empty_mappings) {
          services_with_missing_mappings.push(service.name);
        }
      });

      return services_with_missing_mappings;
    },

    has_saved_mappings(service_id) {
      if (!Reflect.has(this.mappings, service_id)) return false;

        // Check if any field in this service has a non-empty mapping
        return Object.values(Reflect.get(this.mappings, service_id))
          .some(value => value && value !== "");
    },

    fetch_existing_mappings(service_id) {
      axios.get(GET_QUESTIONS_MAPPED_URL, {
        headers: get_headers(),
        params: {
          service_id: service_id,
          form_id: this.form_id
        }
      })
      .then(response => {
        // Parse response properly
        let parsed;
        if (typeof response.data === "string") {
          parsed = JSON.parse(response.data);
        } else {
          parsed = response.data;
        }

        // Check if response has the expected structure
        let existing = [];
        if (parsed && parsed.mappings && Array.isArray(parsed.mappings)) {
          existing = parsed.mappings;
        } else if (Array.isArray(parsed)) {
          existing = parsed; // If response is directly an array
        }

        // Apply saved mappings
        existing.forEach(item => {
          const field = item.required_field;
          if (Reflect.has(this.mappings, service_id) &&
              Reflect.has(Reflect.get(this.mappings, service_id), field)) {
            Reflect.set(
              Reflect.get(this.mappings, service_id),
              field,
              item.question_id.toString()
            );
          }
        });
      })
      .catch(error => {
        this.show_error(`Failed to load existing mappings for service ${service_id}:`, error);
      });
    },

    format_field_name(field) {
      return field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  },
};